import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import Box from '@material-ui/core/Box';
import {
  Button,
  Collapse,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip
} from '@material-ui/core';
import DialogWithEnterAction from '../components/DialogWithEnterAction';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ContentCopyIcon from '@material-ui/icons/FileCopy';
import Typography from '@material-ui/core/Typography';
import { useSnackbar } from 'notistack';

import { ExpandMore } from '../Utils/Expand';
import { copyTextToClipboard } from '../Utils/clipboard_utils';

export { copyTextToClipboard } from '../Utils/clipboard_utils';

const useStyles = makeStyles((theme) => ({
  hint: {
    color: theme.palette.primary.main,
    fontSize: '1em',
    fontWeight: 500
  },

  value: {
    color: theme.palette.primary.main,
    fontSize: '3em',
    fontWeight: 500,
    maxWidth: '100%',
    overflowWrap: 'anywhere',
    whiteSpace: 'normal',
    '[data-info-metric-value] &': {
      fontSize: '2.5em'
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '2em',
      lineHeight: 1.15,
      '[data-info-metric-value] &': {
        fontSize: '2em'
      }
    }
  },
  expandIcon: {
    color: theme.palette.primary.main
  },
  moreIcon: {
    flex: '0 0 auto',
    marginLeft: 3,
    color: 'grey'
  },
  statusBuffer: {
    alignItems: 'center',
    color: theme.palette.primary.main,
    display: 'flex',
    fontSize: '1em',
    fontWeight: 500,
    gap: theme.spacing(0.5),
    maxWidth: '100%',
    minWidth: 0,
    whiteSpace: 'nowrap',
    width: '100%'
  },
  statusBufferText: {
    color: theme.palette.primary.main,
    display: 'block',
    flex: 1,
    lineHeight: 1.35,
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  statusDetailLine: {
    color: theme.palette.primary.main,
    fontWeight: 500,
    marginBottom: theme.spacing(1)
  },
  dialogTitle: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between'
  }
}));

const IRRIGATION_STATUS_SEPARATOR = '|.|';
// Wire tokens stay stable for compatibility; they represent per-zone active irrigation time.
const IRRIGATION_ACTIVE_DURATION_KEYS = ['rt_1', 'rt_2', 'rt_3'];

function translateStatusToken(token, t, i18n, preferShort) {
  const trimmedToken = token.trim();
  const shortToken = `${trimmedToken}_short`;

  if (preferShort && i18n.exists(shortToken)) {
    return t(shortToken);
  }

  return t(trimmedToken);
}

function parseActiveDurationSeconds(token) {
  const match = String(token)
    .trim()
    .match(/^(\d+)s$/);
  return match ? Number(match[1]) : null;
}

function formatActiveDurationSeconds(seconds) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  return [hours, minutes, remainingSeconds].map((part) => String(part).padStart(2, '0')).join(':');
}

function formatCompactActiveDurationSeconds(seconds) {
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours <= 0) {
    return `${minutes}m`;
  }

  return `${hours}h`;
}

function formatClipboardTimestamp(language) {
  return new Intl.DateTimeFormat(language, {
    dateStyle: 'medium',
    timeStyle: 'medium'
  }).format(new Date());
}

function translateStatusPartToken(token, tokens, index, t, i18n, preferShort) {
  const previousToken = tokens[index - 1];
  const activeDurationSeconds = parseActiveDurationSeconds(token);

  if (IRRIGATION_ACTIVE_DURATION_KEYS.includes(previousToken) && activeDurationSeconds !== null) {
    return preferShort
      ? formatCompactActiveDurationSeconds(activeDurationSeconds)
      : formatActiveDurationSeconds(activeDurationSeconds);
  }

  return translateStatusToken(token, t, i18n, preferShort);
}

function parseIrrigationStatusBuffer(value, t, i18n, preferShort) {
  if (!value) return [];

  return value
    .split(IRRIGATION_STATUS_SEPARATOR)
    .map((part) => {
      const tokens = part
        .split('|')
        .map((token) => token.trim())
        .filter(Boolean);

      return tokens
        .map((token, index) => translateStatusPartToken(token, tokens, index, t, i18n, preferShort))
        .join(' ')
        .trim();
    })
    .filter(Boolean);
}

function getActiveDurationTotalLine(value, t) {
  if (!value || !value.includes('irr_rt')) return null;

  const tokens = value
    .split(IRRIGATION_STATUS_SEPARATOR)
    .flatMap((part) => part.split('|').map((token) => token.trim()))
    .filter(Boolean);
  const totalSeconds = tokens.reduce((sum, token, index) => {
    const previousToken = tokens[index - 1];
    const activeDurationSeconds = parseActiveDurationSeconds(token);

    if (IRRIGATION_ACTIVE_DURATION_KEYS.includes(previousToken) && activeDurationSeconds !== null) {
      return sum + activeDurationSeconds;
    }

    return sum;
  }, 0);

  return `${t('irr_tot')} ${formatActiveDurationSeconds(totalSeconds)}`;
}

export function IrrigationStatusBuffer({ value, zoneName }) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const compactParts = parseIrrigationStatusBuffer(value, t, i18n, true);
  const detailParts = parseIrrigationStatusBuffer(value, t, i18n, false);
  const activeDurationTotalLine = getActiveDurationTotalLine(value, t);
  const modalLines = [...detailParts, activeDurationTotalLine].filter(Boolean);
  const copyModalLines = async () => {
    try {
      const timestampLine = `${t('clipboard_timestamp')}: ${formatClipboardTimestamp(i18n.language)}`;
      const zoneLine = zoneName ? `${t('clipboard_zone')}: ${zoneName}` : null;
      await copyTextToClipboard([timestampLine, zoneLine, ...modalLines].filter(Boolean).join('\n'));
      enqueueSnackbar(t('copied_to_clipboard'), { variant: 'success' });
    } catch (_) {
      enqueueSnackbar(t('unknown_error'), { variant: 'error' });
    }
  };
  const compactText = compactParts.join(' · ');

  if (compactParts.length <= 0) {
    return null;
  }

  return (
    <>
      <Box className={classes.statusBuffer}>
        <Typography component="span" className={classes.statusBufferText}>
          {compactText}
        </Typography>
        <IconButton size="small" className={classes.moreIcon} onClick={() => setOpen(true)}>
          <MoreHorizIcon />
        </IconButton>
      </Box>

      <DialogWithEnterAction
        open={open}
        onClose={() => setOpen(false)}
        onEnterAction={() => setOpen(false)}
        aria-labelledby="irrigation-status-title"
      >
        <DialogTitle id="irrigation-status-title" disableTypography>
          <Typography variant="h6" component="div" className={classes.dialogTitle}>
            <span>{t('irrigation_state')}</span>
            <Tooltip title={t('copy_to_clipboard')}>
              <IconButton
                aria-label={t('copy_to_clipboard')}
                color="primary"
                size="small"
                onClick={copyModalLines}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Typography>
        </DialogTitle>
        <DialogContent>
          {modalLines.map((part, idx) => (
            <Typography key={`${part}_${idx}`} className={classes.statusDetailLine}>
              {part}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            {t('close')}
          </Button>
        </DialogActions>
      </DialogWithEnterAction>
    </>
  );
}

export function NormalValue({
  name,
  unit,
  value,
  style,
  off_at = undefined,
  off_label,
  bypass_at = undefined,
  bypass_label,
  ...other
}) {
  const classes = useStyles();
  let { t } = useTranslation();

  if (value === -1 || value === 0xffff) {
    return (
      <label name={name} className={classes.value} style={style}>
        --- {unit}
      </label>
    );
  } else if (off_at !== undefined && value === off_at) {
    return (
      <label name={name} className={classes.value} style={style}>
        {off_label ?? t('off')}
      </label>
    );
  } else if (bypass_at !== undefined && value === bypass_at) {
    return (
      <label name={name} className={classes.value} style={style}>
        {bypass_label ?? t('bypass')}
      </label>
    );
  } else {
    return (
      <label name={name} className={classes.value} style={style}>
        {value} {unit}
      </label>
    );
  }
}

export function FloatValue({ name, unit, value, style, num_digits = 1, ...other }) {
  const adjusted_value = (Number.parseFloat(value) / 10 ** num_digits).toFixed(num_digits);
  const classes = useStyles();
  if (value === -1 || value === 0xffff) {
    return (
      <label name={name} className={classes.value} style={style}>
        --- {unit}
      </label>
    );
  } else {
    return (
      <label name={name} className={classes.value} style={style}>
        {adjusted_value} {unit}
      </label>
    );
  }
}

export function NegativeValue({ name, unit, value, style, ...other }) {
  const adjusted_value = value - 4000;
  const classes = useStyles();
  if (value === -1 || value === 0xffff) {
    return (
      <label name={name} className={classes.value} style={style}>
        --- {unit}
      </label>
    );
  } else {
    return (
      <label name={name} className={classes.value} style={style}>
        {adjusted_value} {unit}
      </label>
    );
  }
}

export function BulletPointSeparator({ style, ...other }) {
  const classes = useStyles();
  return (
    <label className={classes.value} style={style}>
      &nbsp;•&nbsp;
    </label>
  );
}

export function ExternalReadings({
  expand_id,
  readings,
  namePrefix,
  ValueComp,
  num_digits,
  marginProps,
  valueProps,
  extReadsValues,
  onAdd
}) {
  const classes = useStyles();

  // Initialize state from localStorage or default value
  const [expand, setExpand] = useState(() => {
    if (!expand_id) return false;
    const savedState = localStorage.getItem(`expandable_${expand_id}`);
    return savedState !== null ? savedState === 'true' : false;
  });

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    if (expand_id) {
      localStorage.setItem(`expandable_${expand_id}`, expand.toString());
    }
  }, [expand, expand_id]);

  const handleExpandClick = () => {
    setExpand(!expand);
  };

  if (readings.length <= 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, marginTop: marginProps }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <ExpandMore
          expand={expand}
          onClick={() => handleExpandClick()}
          aria-expanded={expand}
          aria-label="show more"
          style={{ marginLeft: -6, padding: 0 }}
        >
          <ExpandMoreIcon fontSize="large" className={classes.expandIcon} />
        </ExpandMore>

        <Collapse in={expand} timeout="auto" unmountOnExit>
          <>
            {readings.map((ex, idx) => (
              <React.Fragment key={`${namePrefix}${ex.addr_sensor}`}>
                {idx > 0 && <BulletPointSeparator style={valueProps} />}
                <ValueComp
                  name={`${namePrefix}${ex.addr_sensor}`}
                  value={ex.value ?? -1}
                  style={{
                    ...valueProps,
                    ...(extReadsValues.extrs.find(
                      (fixed_ex) =>
                        fixed_ex.zone === ex.zone &&
                        fixed_ex.sub_zone === ex.sub_zone &&
                        fixed_ex.type === ex.type &&
                        fixed_ex.gcl_addr === ex.gcl_addr &&
                        fixed_ex.port_id === ex.port_id &&
                        fixed_ex.addr_sensor === ex.addr_sensor
                    )?.exclude_cur_value
                      ? { color: 'grey' }
                      : {})
                  }}
                  num_digits={num_digits}
                />
              </React.Fragment>
            ))}
            <IconButton size="small" className={classes.moreIcon} onClick={onAdd}>
              <MoreHorizIcon />
            </IconButton>
          </>
        </Collapse>
      </div>
    </Box>
  );
}
